import {
  AiAssistantChatHistory,
  AiAssistantChatHistoryPublic,
  AiAssistantListItem,
  CreateAiAssistantModel,
  CreateAiAssistantResponse,
  FetchCoursesShortListModel,
  UploadAIAvatarModel,
} from '../models/AI/AiAssistantModels';
import {
  VideoCostEstimationModel,
  VideoEstimationResponse,
} from '../models/AI/VideoGenerationConfig';
import {ApiResponse, PaginatedApiReponse} from '../models/ApiResponse';
import {
  GeneratedChallengeBranch,
  GeneratedQuestions,
} from '../models/QuestionGeneration/GeneratedQuestions';
import {apiClient, withAuth} from './baseApiClient';

const AI_GENERATION_TIMEOUT_MS = 600000;
class QuestionGenerationAPI {
  public async generateQuestions(
    organizationId: string,
    videoNodeId: string,
    topic: string,
    lessonId: string,
    difficulty: string,
  ): Promise<ApiResponse<GeneratedQuestions>> {
    const headers = await withAuth({});
    return apiClient.post(
      `/videoQuestions/${organizationId}/generate`,
      {videoNodeId, topics: [topic], lessonId, qaDifficultyLevel: difficulty},
      {headers, timeout: AI_GENERATION_TIMEOUT_MS},
    );
  }

  public async generateChallangeBranchAI(
    organizationId: string,
    lessonId: string,
    videoNodeId: string,
  ): Promise<ApiResponse<GeneratedChallengeBranch>> {
    const headers = await withAuth({});
    return apiClient.post(
      `/videoQuestions/${organizationId}/lesson/${lessonId}/videoNode/${videoNodeId}/generate_masks_and_questions`,
      {},
      {headers, timeout: AI_GENERATION_TIMEOUT_MS},
    );
  }

  public async getVideoCostEstimation(
    organizationId: string,
    model: VideoCostEstimationModel,
  ): Promise<ApiResponse<VideoEstimationResponse>> {
    return apiClient.post(
      `/videoQuestions/${organizationId}/get_ai_credits_estimation`,
      model,
    );
  }

  public async createAiAssistant(
    organizationId: string,
    model: CreateAiAssistantModel,
  ): Promise<ApiResponse<CreateAiAssistantResponse>> {
    return apiClient.post(
      `/videoQuestions/${organizationId}/ai_assistant/create`,
      model,
    );
  }

  public async editAiAssistant(
    organizationId: string,
    model: CreateAiAssistantModel,
    assistantId: string,
  ): Promise<ApiResponse<CreateAiAssistantResponse>> {
    return apiClient.post(
      `/videoQuestions/${organizationId}/ai_assistant/update/${assistantId}`,
      model,
    );
  }

  public async fetchCoursesShortList(
    organizationId: string,
    search: string,
  ): Promise<ApiResponse<FetchCoursesShortListModel[]>> {
    return apiClient.get(
      `/course/${organizationId}/simple_list?search=${search}`,
    );
  }

  public async uploadAIAvatar(
    organizationId: string,
    assistantId: string,
    avatar?: File,
  ): Promise<ApiResponse<UploadAIAvatarModel[]>> {
    const form = new FormData();
    if (avatar) {
      form.append('avatar', avatar);
    }
    form.append('assistantId', assistantId);
    return apiClient.post(
      `/videoQuestions/${organizationId}/ai_assistant/upload_avatar`,
      form,
    );
  }

  public async getAiAssistantsList(
    organizationId: string,
    page: number,
    pageSize: number,
  ): Promise<PaginatedApiReponse<AiAssistantListItem>> {
    return apiClient.post(
      `/videoQuestions/${organizationId}/ai_assistant/list`,
      {page, pageSize},
    );
  }

  public async toggleAiAssitantVisibility(
    organizationId: string,
    assistantId: string,
  ): Promise<ApiResponse<any>> {
    return apiClient.post(
      `/videoQuestions/${organizationId}/ai_assistant/${assistantId}/toggle_visibility`,
    );
  }

  public async deleteAiAssistnat(
    organizationId: string,
    assistantId: string,
  ): Promise<ApiResponse<any>> {
    return apiClient.delete(
      `/videoQuestions/${organizationId}/ai_assistant/${assistantId}`,
    );
  }

  public async getAssistantChat(
    organizationId: string,
    assistantId: string,
  ): Promise<ApiResponse<AiAssistantChatHistory>> {
    return apiClient.get(
      `/videoQuestions/${organizationId}/ai_assistant/${assistantId}/history`,
    );
  }

  public async getAssistantPublicChat(
    organizationId: string,
    assistantId: string,
    sessionId: string,
  ): Promise<ApiResponse<AiAssistantChatHistoryPublic>> {
    return apiClient.get(
      `/AiAssistant/public/history/${assistantId}?sessionId=${sessionId}`,
    );
  }
}

export default QuestionGenerationAPI;
